import React from "react";
import classNames from "classnames";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    getNativeInvoiceDisplayStatusLabel,
    NativeInvoiceDisplayStatus,
} from "./getNativeInvoiceDisplayStatus";
import { nonEmptyStringOrNull } from "./nativeInvoiceFormHelpers";
import { STATUS_DOT_CLASS } from "./NativeInvoiceStatusBar";
import styles from "./NativeInvoiceDrawer.module.scss";

export type NativeInvoiceDrawerMode =
    | "create"
    | "edit"
    | "template"
    | "createTemplate";

function workflowLabel(mode: NativeInvoiceDrawerMode): string {
    if (mode === "createTemplate") {
        return "Create template";
    }
    if (mode === "template") {
        return "Edit template";
    }
    if (mode === "edit") {
        return "Edit invoice";
    }
    return "Create invoice";
}

function currentLabel({
    mode,
    invoiceNumber,
    templateName,
    displayStatus,
    documentLabel = "Invoice",
}: {
    mode: NativeInvoiceDrawerMode;
    invoiceNumber?: string;
    templateName?: string;
    displayStatus?: NativeInvoiceDisplayStatus;
    documentLabel?: string;
}): { label: string; statusDot: React.ReactNode } | null {
    if (mode === "template" || mode === "createTemplate") {
        // Templates are a single-step workflow, so the breadcrumb only grows
        // a second crumb once there's an actual name to show.
        const name = nonEmptyStringOrNull(templateName?.trim());
        return name === null ? null : { label: name, statusDot: null };
    }

    const statusDot = displayStatus ? (
        <Tooltip content={getNativeInvoiceDisplayStatusLabel(displayStatus)}>
            <span
                className={classNames(
                    styles.statusDot,
                    STATUS_DOT_CLASS[displayStatus],
                )}
            />
        </Tooltip>
    ) : null;

    return {
        label: invoiceNumber ? `${documentLabel} ${invoiceNumber}` : "Draft",
        statusDot,
    };
}

// The current step is carried by the status bar below the top bar, so the
// breadcrumb never appends it.
export const NativeInvoiceDraftHeader: React.FC<
    Readonly<{
        mode: NativeInvoiceDrawerMode;
        invoiceNumber?: string;
        templateName?: string;
        displayStatus?: NativeInvoiceDisplayStatus;
        rootLabel?: string;
        documentLabel?: string;
    }>
> = ({
    mode,
    invoiceNumber,
    templateName,
    displayStatus,
    rootLabel,
    documentLabel,
}) => {
    const current = currentLabel({
        mode,
        invoiceNumber,
        templateName,
        displayStatus,
        documentLabel,
    });
    const root = rootLabel ?? workflowLabel(mode);
    return (
        <Breadcrumbs
            aria-label="Invoice workflow"
            className={styles.draftHeaderBreadcrumbs}
        >
            {current === null ? (
                <BreadcrumbItem current>{root}</BreadcrumbItem>
            ) : (
                <>
                    <BreadcrumbItem>{root}</BreadcrumbItem>
                    <BreadcrumbItem current leftIcon={current.statusDot}>
                        {current.label}
                    </BreadcrumbItem>
                </>
            )}
        </Breadcrumbs>
    );
};
